(() => {
  'use strict';

  if (window.__ccaCartDrawerRefreshAdapter) return;

  const drawerId = 'CartDrawer';
  const sectionSelector = '[data-section-type="cart-template"]';
  const activeClass = () => window.themeCore?.utils?.cssClasses?.active || 'is-active';
  const delay = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const adapter = {
    start() {
      const drawer = document.getElementById(drawerId);
      if (!drawer) return false;

      let open = drawer.classList.contains(activeClass());
      let inFlight = null;
      let requestToken = 0;
      let status = null;
      let content = null;
      let contentWasHidden = false;

      const isOpen = () => drawer.classList.contains(activeClass());
      const sectionIds = () => [...document.querySelectorAll(sectionSelector)]
        .map((section) => section.dataset.sectionId)
        .filter(Boolean);

      const setStatus = (message) => {
        if (!status) {
          status = document.createElement('div');
          status.className = 'cca-cart-refresh-status';
          status.setAttribute('role', 'status');
          status.setAttribute('aria-live', 'polite');
          status.style.cssText = 'padding:1rem;';
          drawer.querySelector('.js-cart-content')?.before(status);
        }
        status.textContent = message;
        status.hidden = !message;
      };

      const setBusy = (busy) => {
        content = content || drawer.querySelector('.js-cart-content');
        drawer.setAttribute('aria-busy', String(busy));
        if (!content) return;
        if (busy) {
          contentWasHidden = content.hidden;
          content.hidden = true;
          setStatus('Updating your cart…');
        } else {
          content.hidden = contentWasHidden;
          setStatus('');
        }
      };

      const readJson = async (url) => {
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error(`Cart refresh failed with ${response.status}`);
        return response.json();
      };

      const readSections = async () => {
        const ids = sectionIds();
        if (!ids.length) throw new Error('Cart section is unavailable');
        const url = new URL(window.location.href);
        url.searchParams.set('sections', ids.join(','));
        url.searchParams.set('lazyload', 'false');
        return readJson(url.href);
      };

      const sectionForDrawer = (sections) => {
        const id = [...document.querySelectorAll(sectionSelector)]
          .find((section) => section.dataset.sectionModification === 'drawer')?.dataset.sectionId;
        return id && sections[id] ? sections[id] : Object.values(sections)[0];
      };

      const applySection = (html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = parsed.querySelector('[data-section-type="cart-template"]');
        const target = drawer.querySelector(sectionSelector) || drawer;
        const sourceContent = source?.querySelector('.js-cart-content');
        const targetContent = target.querySelector('.js-cart-content');
        if (!source || !sourceContent || !targetContent) throw new Error('Cart section response is incomplete');

        const focused = document.activeElement;
        const focusedSelector = focused && target.contains(focused)
          ? (focused.id ? `#${CSS.escape(focused.id)}` : null)
          : null;
        target.classList.toggle('is-empty', source.classList.contains('is-empty'));
        targetContent.innerHTML = sourceContent.innerHTML;

        for (const selector of ['.cart-drawer__summary', '.cart-drawer__footer']) {
          const sourcePart = source.querySelector(selector);
          const targetPart = target.querySelector(selector);
          if (sourcePart && targetPart) targetPart.replaceWith(sourcePart.cloneNode(true));
          else if (!sourcePart && targetPart) targetPart.remove();
          else if (sourcePart && !targetPart) target.append(sourcePart.cloneNode(true));
        }

        if (focusedSelector) target.querySelector(focusedSelector)?.focus();
        return source.querySelectorAll('[data-cart-item]').length;
      };

      const refresh = async () => {
        if (inFlight || !isOpen()) return inFlight;
        const token = ++requestToken;
        setBusy(true);
        inFlight = (async () => {
          let failureMessage = '';
          try {
            for (let attempt = 0; attempt < 2; attempt += 1) {
              const [cart, sections] = await Promise.all([readJson(`${window.Shopify?.routes?.root || '/'}cart.js`), readSections()]);
              const html = sectionForDrawer(sections);
              const cartCount = Number(cart.item_count || 0);
              if (token !== requestToken || !isOpen()) return;
              const itemCount = html ? applySection(html) : 0;
              window.themeCore?.EventBus?.emit('header:update-item-count', cart);
              if (attempt === 0 && (cartCount !== itemCount || cartCount === 0)) {
                await delay(250);
                continue;
              }
              return;
            }
          } catch (error) {
            failureMessage = 'Cart refresh unavailable. Your cart was not changed.';
          } finally {
            if (token === requestToken) {
              setBusy(false);
              if (failureMessage && isOpen()) setStatus(failureMessage);
              inFlight = null;
            }
          }
        })();
        return inFlight;
      };

      const onOpen = () => {
        if (!open) {
          open = true;
          refresh();
        }
      };
      const onStateChange = () => {
        const nextOpen = isOpen();
        if (nextOpen && !open) onOpen();
        open = nextOpen;
      };

      window.themeCore?.EventBus?.listen(`Toggle:${drawerId}:open`, onOpen);
      new MutationObserver(onStateChange).observe(drawer, { attributes: true, attributeFilter: ['class'] });
      window.__ccaCartDrawerRefreshAdapter = { refresh };
      return true;
    }
  };

  let bindAttempts = 0;
  const bind = () => {
    if (adapter.start()) return;
    bindAttempts += 1;
    if (bindAttempts < 30) window.setTimeout(bind, 100);
  };
  bind();
})();
